import { useCallback, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

export default function Lightbox({ items, index, onClose, onIndex }) {
  const item = items && items[index];
  const isOpen = Boolean(item);

  const prev = useCallback(() => {
    if (items && items.length) onIndex((index - 1 + items.length) % items.length);
  }, [index, items, onIndex]);
  const next = useCallback(() => {
    if (items && items.length) onIndex((index + 1) % items.length);
  }, [index, items, onIndex]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [isOpen, onClose, prev, next]);

  return (
    <AnimatePresence>
      {item && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] flex flex-col items-center justify-center bg-joell-black/95 p-4 backdrop-blur-md"
          onClick={onClose}
          data-testid="gallery-lightbox"
        >
          <button className="absolute right-4 top-4 flex h-11 w-11 items-center justify-center border border-white/20 text-white transition-colors hover:border-joell-yellow hover:text-joell-yellow" onClick={onClose} aria-label="Close" data-testid="lightbox-close-button">
            <X size={20} />
          </button>

          <button
            className="absolute left-2 top-1/2 z-10 flex h-11 w-11 -translate-y-1/2 items-center justify-center border border-white/20 bg-joell-black/60 text-white transition-colors hover:border-joell-yellow hover:text-joell-yellow"
            onClick={(e) => { e.stopPropagation(); prev(); }}
            aria-label="Previous image"
            data-testid="lightbox-prev-button"
          >
            <ChevronLeft size={20} />
          </button>
          <button
            className="absolute right-2 top-1/2 z-10 flex h-11 w-11 -translate-y-1/2 items-center justify-center border border-white/20 bg-joell-black/60 text-white transition-colors hover:border-joell-yellow hover:text-joell-yellow"
            onClick={(e) => { e.stopPropagation(); next(); }}
            aria-label="Next image"
            data-testid="lightbox-next-button"
          >
            <ChevronRight size={20} />
          </button>

          {item.video ? (
            <video
              key={item.src}
              src={item.src}
              controls
              autoPlay
              playsInline
              className="max-h-[78vh] max-w-full border border-white/15"
              onClick={(e) => e.stopPropagation()}
              data-testid="lightbox-video"
            />
          ) : (
            <motion.img
              key={item.src}
              initial={{ opacity: 0, scale: 0.97 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.25 }}
              src={item.src}
              alt={item.title || "Project photo"}
              className="max-h-[78vh] max-w-full border border-white/15 object-contain"
              onClick={(e) => e.stopPropagation()}
              data-testid="lightbox-image"
            />
          )}
          {item.title && (
            <p className="mt-4 font-mono text-xs font-bold uppercase tracking-[0.2em] text-slate-300">{item.title}</p>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
}
